import { useMutation, useQuery } from "@apollo/client";
import { createAjv } from "@jsonforms/core";
import { Breadcrumb, Button } from "pepsico-ds";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ToastNotification from "../../components/common/toastNotification/ToastNotification";
import JsonFormsWrapper from "../../components/jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { createKeywordGroup } from "../../graphql/mutations/createKeywordGroup";
import { updateKeywordGroup } from "../../graphql/mutations/updateKeywordGroup";
import { listStores } from "../../graphql/queries/listStores";
import { useToast } from "../../hooks/useToast";
import {
  createStoreFormSchema,
  createStoreInfoUISchema,
  storeAddressUISchema,
  storeFormSchema,
  storeInfoUISchema,
} from "./storeFormSchema";
import "./storeManagement.scss";
import { formatValidationErrors } from "./storeValidationErrors";

const OUTLET_TYPE = "OUTLET";

const ajv = createAjv({ useDefaults: true });
const validateStore = ajv.compile(storeFormSchema);
const validateCreateStore = ajv.compile(createStoreFormSchema);

const generateSlug = (name) =>
  (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const initialFormData = {
  name: "",
  slug: "",
  imageUrl: null,
  keywords: [],
  metadata: {
    store_id: "",
    name: "",
    retailer: "",
    address: "",
    postcode: "",
    city: "",
    country: "",
    state: "",
    google_maps_link: "",
    geolocation: {
      latitude: null,
      longitude: null,
    },
  },
};

export default function StoreDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const { toast, showToast, closeToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  const [formData, setFormData] = useState(initialFormData);
  const [isFormReady, setIsFormReady] = useState(!isEditMode);

  const [executeCreateKeyword] = useMutation(createKeywordGroup);
  const [executeUpdateKeyword] = useMutation(updateKeywordGroup);

  const { data: storesData, loading: storesLoading } = useQuery(listStores, {
    fetchPolicy: "network-only",
    skip: !isEditMode,
  });

  useEffect(() => {
    if (!isEditMode) {
      setFormData(initialFormData);
      setIsFormReady(true);
      return;
    }
    setIsFormReady(false);
    if (storesData?.listKeywordGroups) {
      const store = storesData.listKeywordGroups.find((s) => s.id === id);
      if (store) {
        const meta =
          typeof store.metadata === "string"
            ? JSON.parse(store.metadata)
            : store.metadata || {};
        setFormData({
          id: store.id,
          name: store.name || "",
          slug: store.slug || generateSlug(store.name || ""),
          imageUrl: store.imageUrl || null,
          category: store.category || "",
          keywords: store.keywords || [],
          metadata: {
            store_id: meta.store_id || "",
            name: meta.name || "",
            retailer: meta.retailer || "",
            address: meta.address || "",
            postcode: meta.postcode ? String(meta.postcode) : "",
            city: meta.city || "",
            country: meta.country || "",
            state: meta.state || "",
            google_maps_link: meta.google_maps_link || "",
            geolocation: {
              latitude: meta.geolocation?.latitude ?? null,
              longitude: meta.geolocation?.longitude ?? null,
            },
          },
        });
        setIsFormReady(true);
      }
    }
  }, [isEditMode, storesData, id]);

  const handleFormChange = useCallback((data) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        ...data,
        id: prev.id,
        metadata: {
          ...prev.metadata,
          ...(data.metadata || {}),
          geolocation: {
            ...prev.metadata?.geolocation,
            ...(data.metadata?.geolocation || {}),
          },
        },
      };
      const currentName = data.name !== undefined ? data.name : prev.name;
      updated.slug = generateSlug(currentName);
      if (data.name !== undefined) {
        updated.metadata.name = data.name;
      }
      return updated;
    });
  }, []);

  const handleCancel = () => {
    navigate("/store-management");
  };

  useEffect(() => {
    const nextKeywords = [formData.name?.trim(), formData.metadata?.retailer?.trim()]
      .filter(Boolean)
      .reduce((acc, value) => (acc.includes(value) ? acc : [...acc, value]), []);
    if (JSON.stringify(formData.keywords) === JSON.stringify(nextKeywords)) {
      return;
    }
    setFormData((prev) => ({ ...prev, keywords: nextKeywords }));
  }, [formData.name, formData.metadata?.retailer, formData.keywords]);

  const jsonFormsConfig = useMemo(() => ({ showValidation }), [showValidation]);

  const handleSave = async () => {
    const validator = isEditMode ? validateStore : validateCreateStore;
    const isValid = validator(formData);
    if (!isValid) {
      setShowValidation(true);
      const errorMessages = formatValidationErrors(validator.errors || []);
      showToast(
        "error",
        "Validation Error",
        errorMessages.length > 0
          ? errorMessages.join(", ")
          : "Please fix the highlighted errors before saving"
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        slug: generateSlug(formData.name),
        imageUrl: formData.imageUrl || null,
        type: OUTLET_TYPE,
        ...(isEditMode ? { category: formData.category || null } : {}),
        keywords:
          formData.keywords?.length > 0 ? formData.keywords : [formData.name],
        metadata: {
          store_id: formData.metadata.store_id,
          name: formData.metadata.name || formData.name,
          retailer: formData.metadata.retailer || null,
          address: formData.metadata.address,
          postcode: formData.metadata.postcode,
          city: formData.metadata.city,
          country: formData.metadata.country,
          state: formData.metadata.state || null,
          google_maps_link: formData.metadata.google_maps_link || null,
          geolocation: {
            latitude: parseFloat(formData.metadata.geolocation.latitude),
            longitude: parseFloat(formData.metadata.geolocation.longitude),
          },
        },
      };

      if (isEditMode && formData.id) {
        await executeUpdateKeyword({
          variables: { id: formData.id, input: payload },
        });
        showToast("success", "Store Updated", "Store has been updated successfully");
      } else {
        await executeCreateKeyword({
          variables: { input: payload },
        });
        showToast("success", "Store Created", "Store has been created successfully");
      }

      setIsSubmitting(false);
      setTimeout(() => navigate("/store-management"), 1200);
    } catch (error) {
      setIsSubmitting(false);
      showToast(
        "error",
        "Error",
        error.message || "An error occurred while saving the store"
      );
    }
  };

  const breadcrumbs = [
    { label: "Store Management", href: "/store-management" },
    {
      label: isEditMode ? formData.name || "Edit Store" : "Add Store",
      href: "#",
    },
  ];

  return (
    <div className="container">
      <div className="wrap">
        <Breadcrumb breadcrumbs={breadcrumbs} activeIndex={1} />

        {isEditMode && (storesLoading || !isFormReady) ? (
          <div className="store-detail-page">
            <p>Loading store data...</p>
          </div>
        ) : (
          <div className="store-detail-page">
            <div className="store-detail-section">
              <h3 className="section-title">Store information</h3>
              <JsonFormsWrapper
                jsonschema={isEditMode ? storeFormSchema : createStoreFormSchema}
                uischema={isEditMode ? storeInfoUISchema : createStoreInfoUISchema}
                data={formData}
                onChange={handleFormChange}
                config={jsonFormsConfig}
              />
            </div>

            <div className="store-detail-section store-address-section">
              <h3 className="section-title">Store Address</h3>
              <JsonFormsWrapper
                jsonschema={isEditMode ? storeFormSchema : createStoreFormSchema}
                uischema={storeAddressUISchema}
                data={formData}
                onChange={handleFormChange}
                config={jsonFormsConfig}
              />
            </div>

            <div className="store-detail-actions">
              <Button
                text="Cancel"
                variant="secondary"
                size="medium"
                onClick={handleCancel}
                disabled={isSubmitting}
              />
              <Button
                text="Save"
                variant="primary"
                size="medium"
                onClick={handleSave}
                disabled={isSubmitting}
              />
            </div>
          </div>
        )}

        <ToastNotification {...toast} handleClose={closeToast} />
      </div>
    </div>
  );
}
